import type { ReactNode } from 'react';
import { Button, type ButtonVariant } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface ActionButtonProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  /** Anything but `undefined` turns the action off and states the reason. */
  disabledReason?: string | undefined;
  /** Shown on hover while the action works. */
  hint?: string;
  variant?: ButtonVariant;
  iconOnly?: boolean;
}

/**
 * An action that explains itself when it cannot run.
 *
 * A disabled button swallows pointer events, so the tooltip hangs off a wrapper
 * that stays interactive — otherwise the reason would be unreachable exactly
 * when it matters.
 */
export function ActionButton({
  label,
  icon,
  onClick,
  disabledReason,
  hint,
  variant = 'outline',
  iconOnly = false,
}: ActionButtonProps) {
  const disabled = Boolean(disabledReason);
  const tip = disabledReason ?? hint;

  const button = iconOnly ? (
    <Button
      variant={variant}
      size="icon-sm"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
    </Button>
  ) : (
    <Button variant={variant} size="sm" disabled={disabled} onClick={onClick}>
      {icon}
      {label}
    </Button>
  );

  if (!tip) return button;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={disabled ? 0 : -1} className="inline-flex rounded-md">
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent>{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
